// TaperAdvisorCard — informational "what to discuss with your clinician"
// surface on the Taper screen. Phase 5 PR-B §F3.
//
// The card renders only when the server signals the user is enrolled in
// the `taper_advisor_v1` feature flag (GET /api/ai/taper-advisor returns
// `{ enabled: true }`). Tapping the button POSTs to the same endpoint;
// the server enforces 5/day rate-limit + JSON validation + ±30% guard,
// so the client treats whatever shape lands as authoritative.
//
// Result is kept in local state for ~30 minutes so the user can re-read
// it without burning a daily call. The "informational only — run this
// past your clinician" footer is permanent.

import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { useApi } from '../../hooks/useApi';
import { apiPost, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';

interface AvailabilityResponse {
  enabled: boolean;
}

interface AdvisorResult {
  suggestedTargetMg: number;
  confidence: 'low' | 'med' | 'high';
  rationale: string;
  redFlags: string[];
  isFallback?: boolean;
}

const RESULT_TTL_MS = 30 * 60 * 1000;

function confidenceDotColor(c: AdvisorResult['confidence']): string {
  if (c === 'high') return 'var(--color-streak)';
  if (c === 'med') return 'var(--color-accent)';
  return 'var(--color-warning)';
}

function confidenceLabel(c: AdvisorResult['confidence']): string {
  if (c === 'high') return 'High confidence';
  if (c === 'med') return 'Medium confidence';
  return 'Low confidence';
}

export function TaperAdvisorCard() {
  const toast = useToast();
  const availabilityRes = useApi<AvailabilityResponse>('/api/ai/taper-advisor');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AdvisorResult | null>(null);
  const [resultAt, setResultAt] = useState<number>(0);

  // Auto-clear the result after 30 minutes so the next visit starts fresh.
  useEffect(() => {
    if (!result) return;
    const remaining = Math.max(0, RESULT_TTL_MS - (Date.now() - resultAt));
    const t = window.setTimeout(() => setResult(null), remaining);
    return () => window.clearTimeout(t);
  }, [result, resultAt]);

  const handleAsk = useCallback(async () => {
    setBusy(true);
    try {
      const body = await apiPost<AdvisorResult>('/api/ai/taper-advisor');
      setResult(body);
      setResultAt(Date.now());
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 429) {
        toast.push('You have used your 5 daily checks — try again tomorrow.');
      } else if (err instanceof ApiClientError && err.status === 404) {
        toast.push('Taper advisor is not available for your account.');
      } else {
        toast.push('Could not get a suggestion — please try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [toast]);

  if (availabilityRes.status !== 'ok' || !availabilityRes.data.enabled) {
    return null;
  }

  return (
    <Card accentLeft>
      <h3 className="text-h3" style={{ marginBlockEnd: 'var(--space-2)' }}>
        Next-week thinking partner
      </h3>
      <p className="muted text-caption" style={{ marginBlockEnd: 'var(--space-3)' }}>
        A short, structured sketch you can bring to your clinician. Not a prescription, not
        medical advice — just one starting point to discuss.
      </p>

      {!result ? (
        <Button
          variant="secondary"
          block
          loading={busy}
          onClick={() => void handleAsk()}
        >
          Suggest where I should go next week
        </Button>
      ) : (
        <div>
          {busy ? (
            <div className="skeleton-stack">
              <Skeleton height={20} />
              <Skeleton height={16} width="80%" />
            </div>
          ) : (
            <>
              <div
                className="row"
                style={{
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginBlockEnd: 'var(--space-2)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-block',
                    inlineSize: '10px',
                    blockSize: '10px',
                    borderRadius: '50%',
                    background: confidenceDotColor(result.confidence),
                  }}
                />
                <p className="text-caption muted" style={{ margin: 0 }}>
                  {confidenceLabel(result.confidence)}
                </p>
              </div>
              <p className="stat-card-value" style={{ marginBlock: '0 var(--space-1)' }}>
                {result.suggestedTargetMg.toFixed(1)} <span className="dim">mg/day</span>
              </p>
              <p style={{ marginBlock: 'var(--space-2) var(--space-3)' }}>
                {result.rationale}
              </p>
              {result.redFlags.length > 0 ? (
                <div style={{ marginBlockEnd: 'var(--space-3)' }}>
                  <p
                    className="text-caption muted"
                    style={{
                      marginBlockEnd: 'var(--space-1)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Discuss with your clinician
                  </p>
                  <ul style={{ paddingInlineStart: 'var(--space-5)', margin: 0 }}>
                    {result.redFlags.map((f) => (
                      <li
                        key={f}
                        style={{
                          color: 'var(--color-ink-secondary)',
                          lineHeight: 1.5,
                          marginBlockEnd: 'var(--space-1)',
                        }}
                      >
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <Button
                variant="tertiary"
                onClick={() => setResult(null)}
              >
                Dismiss
              </Button>
            </>
          )}
        </div>
      )}

      <p
        className="text-caption muted"
        style={{ marginBlockStart: 'var(--space-3)' }}
      >
        This is informational only.{' '}
        <a
          href="/app/more#share-provider"
          style={{ color: 'inherit', textDecoration: 'underline' }}
        >
          Run this past your clinician.
        </a>
      </p>
    </Card>
  );
}
